import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './auth/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { RequireAuth } from './auth/RequireAuth';
import { RequireRole } from './auth/RequireRole';
import { AppShell } from './layout/AppShell';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CustomersListPage } from './pages/customers/CustomersListPage';
import { CustomerDetailPage } from './pages/customers/CustomerDetailPage';
import { PlatformSettingsPage } from './pages/settings/PlatformSettingsPage';
import { MasterCatalogPage } from './pages/catalog/MasterCatalogPage';
import { ShopPage } from './pages/orders/ShopPage';
import { OrdersListPage } from './pages/orders/OrdersListPage';
import { OrderDetailPage } from './pages/orders/OrderDetailPage';
import { ApprovalQueuePage } from './pages/orders/ApprovalQueuePage';
import { LoansPage } from './pages/loans/LoansPage';
import { LoanDetailPage } from './pages/loans/LoanDetailPage';
import { ProcurementPage } from './pages/procurement/ProcurementPage';
import { DeliveryPage } from './pages/assets/DeliveryPage';
import { AssetsListPage } from './pages/assets/AssetsListPage';
import { AssetDetailPage } from './pages/assets/AssetDetailPage';
import { InvoicesListPage } from './pages/invoicing/InvoicesListPage';
import { InvoiceDetailPage } from './pages/invoicing/InvoiceDetailPage';
import { CreditNotesListPage } from './pages/invoicing/CreditNotesListPage';
import { CreditNoteDetailPage } from './pages/invoicing/CreditNoteDetailPage';
import { NotificationPreferencesPage } from './pages/NotificationPreferencesPage';
import { ProfitLossPage } from './pages/ProfitLossPage';
import { MspUsersPage } from './pages/MspUsersPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { NotFoundPage } from './pages/NotFoundPage';

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route element={<RequireAuth />}>
                <Route element={<AppShell />}>
                  <Route path="/" element={<DashboardPage />} />

                  <Route element={<RequireRole roles={['MSP_ADMIN']} />}>
                    <Route path="/customers" element={<CustomersListPage />} />
                  </Route>

                  <Route element={<RequireRole roles={['MSP_ADMIN', 'CUSTOMER_ADMIN', 'REPORTS_VIEWER']} />}>
                    <Route path="/customers/:orgId" element={<CustomerDetailPage />} />
                  </Route>

                  <Route element={<RequireRole roles={['MSP_ADMIN']} />}>
                    <Route path="/settings" element={<PlatformSettingsPage />} />
                    <Route path="/catalog" element={<MasterCatalogPage />} />
                    <Route path="/procurement" element={<ProcurementPage />} />
                    <Route path="/delivery" element={<DeliveryPage />} />
                    <Route path="/profit-loss" element={<ProfitLossPage />} />
                    <Route path="/msp-users" element={<MspUsersPage />} />
                    <Route path="/audit-log" element={<AuditLogPage />} />
                  </Route>

                  <Route element={<RequireRole roles={['MSP_ADMIN', 'CUSTOMER_ADMIN']} />}>
                    <Route path="/assets" element={<AssetsListPage />} />
                    <Route path="/assets/:assetId" element={<AssetDetailPage />} />
                    <Route path="/invoices" element={<InvoicesListPage />} />
                    <Route path="/invoices/:invoiceId" element={<InvoiceDetailPage />} />
                    <Route path="/credit-notes" element={<CreditNotesListPage />} />
                    <Route path="/credit-notes/:creditNoteId" element={<CreditNoteDetailPage />} />
                  </Route>

                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/orders" element={<OrdersListPage />} />
                  <Route path="/orders/:orderId" element={<OrderDetailPage />} />
                  <Route path="/approvals" element={<ApprovalQueuePage />} />
                  <Route path="/loans" element={<LoansPage />} />
                  <Route path="/loans/:loanId" element={<LoanDetailPage />} />
                  <Route path="/notification-preferences" element={<NotificationPreferencesPage />} />

                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
